import type { FsnbWorkCard } from '@shared/api';
import { compareLines, qty } from '../helpers';
import {
  DiffRow,
  Grid,
  Muted,
  Name,
  Section,
  SectionTitle,
  TableWrap,
} from '../styles';

export function CompareBody({ before, after }: { before: FsnbWorkCard; after: FsnbWorkCard }) {
  const lines = compareLines(before.resources, after.resources);
  const changedLines = lines.filter((l) => l.state !== 'same').length;
  const contentBefore = new Set(before.work.content);
  const contentAfter = new Set(after.work.content);
  const contentAdded = after.work.content.filter((c) => !contentBefore.has(c));
  const contentRemoved = before.work.content.filter((c) => !contentAfter.has(c));
  const fieldRows: Array<[string, string | null, string | null]> = [
    ['Наименование', before.work.name, after.work.name],
    ['Измеритель', before.work.unit, after.work.unit],
    ['НР', before.work.nr_code, after.work.nr_code],
    ['СП', before.work.sp_code, after.work.sp_code],
  ];
  return (
    <>
      <Name>{after.work.name}</Name>
      <Section>
        <SectionTitle>Что изменилось</SectionTitle>
        {fieldRows
          .filter(([, b, a]) => b !== a)
          .map(([label, b, a]) => (
            <DiffRow key={label}>
              <span className="label">{label}</span>
              <span>
                <del>{b ?? '—'}</del> → <ins>{a ?? '—'}</ins>
              </span>
            </DiffRow>
          ))}
        {(contentAdded.length > 0 || contentRemoved.length > 0) && (
          <DiffRow>
            <span className="label">Состав работ</span>
            <span>
              {contentRemoved.map((c) => (
                <div key={`r-${c}`}>
                  <del>{c}</del>
                </div>
              ))}
              {contentAdded.map((c) => (
                <div key={`a-${c}`}>
                  <ins>{c}</ins>
                </div>
              ))}
            </span>
          </DiffRow>
        )}
        {/* Изменённые строки состава — сразу здесь: в полной таблице ниже
            исключённый ресурс стоит последним и в длинной норме не виден. */}
        {lines
          .filter((l) => l.state !== 'same')
          .map((l, i) => (
            <DiffRow key={`res-${l.code}-${i}`}>
              <span className="label">
                {l.state === 'added'
                  ? 'Новый ресурс'
                  : l.state === 'removed'
                    ? 'Исключён'
                    : 'Расход'}
              </span>
              <span>
                {l.code} {l.name}:{' '}
                {l.state === 'added' ? (
                  <ins>
                    {qty(l.after)} {l.unit}
                  </ins>
                ) : l.state === 'removed' ? (
                  <del>
                    {qty(l.before)} {l.unit}
                  </del>
                ) : (
                  <>
                    <del>{qty(l.before)}</del> → <ins>{qty(l.after)}</ins> {l.unit}
                  </>
                )}
              </span>
            </DiffRow>
          ))}
        {changedLines === 0 &&
          fieldRows.every(([, b, a]) => b === a) &&
          contentAdded.length === 0 &&
          contentRemoved.length === 0 && (
            <Muted>
              Видимых различий нет — изменилась, например, только последовательность строк.
            </Muted>
          )}
      </Section>
      <Section>
        <SectionTitle>
          Ресурсы целиком: было → стало
          {changedLines > 0 ? ` (изменений: ${changedLines})` : ''}
        </SectionTitle>
        <TableWrap>
          <Grid>
            <thead>
              <tr>
                <th>Шифр</th>
                <th>Ресурс</th>
                <th>Ед. изм.</th>
                <th className="num">Было</th>
                <th className="num">Стало</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.code}-${i}`} className={l.state}>
                  <td className="code">{l.code}</td>
                  <td>{l.name}</td>
                  <td>{l.unit}</td>
                  <td className="num">{qty(l.before)}</td>
                  <td className="num after">{qty(l.after)}</td>
                </tr>
              ))}
            </tbody>
          </Grid>
        </TableWrap>
      </Section>
    </>
  );
}
